import { format } from 'date-fns'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TimeSeriesPoint } from '@/api/hooks/use-analytics'

interface RateChartProps {
  data: TimeSeriesPoint[]
  title: string
  dataKey: keyof TimeSeriesPoint
  secondaryKey?: keyof TimeSeriesPoint
  color: string
  secondaryColor?: string
  dangerLine?: number
}

export function RateChart({
  data,
  title,
  dataKey,
  secondaryKey,
  color,
  secondaryColor,
  dangerLine,
}: RateChartProps): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-text-primary">{title}</h3>
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-3)" />
          <XAxis
            dataKey="timestamp"
            tickFormatter={(v: string) => format(new Date(v), 'MMM d')}
            stroke="var(--color-text-tertiary)"
            fontSize={12}
          />
          <YAxis
            stroke="var(--color-text-tertiary)"
            fontSize={12}
            tickFormatter={(v: number) => `${v}%`}
          />
          <Tooltip
            labelFormatter={(v: string) => format(new Date(v), 'MMM d, yyyy HH:mm')}
            formatter={(v: number) => [`${v.toFixed(2)}%`]}
            contentStyle={{
              backgroundColor: 'var(--color-surface-0)',
              border: '1px solid var(--color-surface-3)',
              borderRadius: '8px',
              fontSize: '12px',
            }}
          />
          <Line
            type="monotone"
            dataKey={dataKey as string}
            stroke={color}
            strokeWidth={2}
            dot={false}
          />
          {secondaryKey && secondaryColor && (
            <Line
              type="monotone"
              dataKey={secondaryKey as string}
              stroke={secondaryColor}
              strokeWidth={2}
              dot={false}
            />
          )}
          {dangerLine !== undefined && (
            <ReferenceLine
              y={dangerLine}
              stroke="var(--color-danger)"
              strokeDasharray="4 4"
              label={{
                value: `${dangerLine}%`,
                position: 'right',
                fill: 'var(--color-danger)',
                fontSize: 11,
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
